import React from 'react';
import { format, addDays } from 'date-fns';
import { cn } from '@/lib/utils';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import { ColumnResizeHandle } from '../ColumnResizeHandle';
import { GridHeaderLegend } from './GridHeaderLegend';
import { ALL_DAYS, getMinColumnWidth, isWeekendDay, type DayName } from '../timesheetGridUtils';
import { useFeatureFlags } from '@/contexts/FeatureFlagContext';

export interface GridHeaderColumnsProps {
  visibleDays: readonly DayName[];
  currentWeekStart: Date;
  weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  nonWorkingDays: Set<string>;
  columnWidths: Record<string, number>;
  handleResizeStart: (e: React.MouseEvent, columnKey: string) => void;
  registerColumnHeader: (columnKey: string) => (el: HTMLTableCellElement | null) => void;
  headerRowRef: React.RefObject<HTMLTableRowElement | null>;
  spacerHeaderRef: React.RefObject<HTMLTableCellElement | null>;
  organizationSettings: OrganisationTimesheetSettings | undefined;
  overtimeEnabled: boolean;
  isDaily: boolean;
  isReadOnly: boolean;
}

/** Column heading row of the timesheet grid. */
export function GridHeaderColumns({
  visibleDays,
  currentWeekStart,
  weekStartDay,
  nonWorkingDays,
  columnWidths,
  handleResizeStart,
  registerColumnHeader,
  headerRowRef,
  spacerHeaderRef,
  organizationSettings,
  overtimeEnabled,
  isDaily,
  isReadOnly,
}: GridHeaderColumnsProps) {
  const { hoursToComplete } = useFeatureFlags();
  const showHoursToComplete = hoursToComplete && organizationSettings?.showHoursToComplete;

  return (
    <tr ref={headerRowRef} className="border-b bg-muted">
      <th
        className="relative p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm"
        ref={registerColumnHeader('project')}
        style={{ width: columnWidths.project, minWidth: getMinColumnWidth('project') }}
      >
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
          <span>Project{!isReadOnly && <span className="text-destructive ml-0.5">*</span>}</span>
          {/* Status Legend — inline to the right of Project title */}
          <GridHeaderLegend showNonWorkingKey={nonWorkingDays.size > 0} />
        </div>
        <ColumnResizeHandle columnKey="project" onResizeStart={handleResizeStart} />
      </th>
      {/* Cost Centre column */}
      {organizationSettings?.showCostCentre !== false && (
        <th
          className="relative p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm"
          ref={registerColumnHeader('costCentre')}
          style={{ width: columnWidths.costCentre, minWidth: getMinColumnWidth('costCentre') }}
        >
          Cost Centre{organizationSettings?.requireCostCentre && !isReadOnly && <span className="text-destructive ml-0.5">*</span>}
          <ColumnResizeHandle columnKey="costCentre" onResizeStart={handleResizeStart} />
        </th>
      )}
      {/* Role column */}
      {organizationSettings?.showRole && (
        <th
          className="relative p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm"
          ref={registerColumnHeader('role')}
          style={{ width: columnWidths.role, minWidth: getMinColumnWidth('role') }}
        >
          Role{organizationSettings?.requireRole && !isReadOnly && <span className="text-destructive ml-0.5">*</span>}
          <ColumnResizeHandle columnKey="role" onResizeStart={handleResizeStart} />
        </th>
      )}
      {/* Work Type column */}
      {overtimeEnabled && (
        <th
          className="relative p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm"
          ref={registerColumnHeader('workType')}
          style={{ width: columnWidths.workType, minWidth: getMinColumnWidth('workType') }}
        >
          Work Type
          <ColumnResizeHandle columnKey="workType" onResizeStart={handleResizeStart} />
        </th>
      )}
      {!isDaily && (
        <th
          className="relative p-1.5 sm:p-2 text-left font-medium text-xs sm:text-sm"
          ref={registerColumnHeader('description')}
          style={{ width: columnWidths.description, minWidth: getMinColumnWidth('description') }}
        >
          Description{organizationSettings?.requireDescription === 'always' && !isReadOnly && <span className="text-destructive ml-0.5">*</span>}
          <ColumnResizeHandle columnKey="description" onResizeStart={handleResizeStart} />
        </th>
      )}
      {visibleDays.map((day) => {
        // Calculate the actual date for this day based on its position in the week
        const dayIndex = ALL_DAYS.indexOf(day);
        const weekStartDayIndex = weekStartDay;
        const daysFromStart = dayIndex >= weekStartDayIndex
          ? dayIndex - weekStartDayIndex
          : 7 - weekStartDayIndex + dayIndex;
        const cellDate = addDays(currentWeekStart, daysFromStart);
        const isNonWorking = nonWorkingDays.has(day);

        return (
          <th key={day} className={cn(
            'p-1.5 sm:p-2 text-center font-medium text-xs sm:text-sm',
            isDaily ? 'w-[80px] sm:w-[100px]' : 'w-[54px] sm:w-[60px]',
            isWeekendDay(day) && 'bg-muted',
            isNonWorking && 'bg-amber-50/60 dark:bg-amber-900/20'
          )}>
            {format(cellDate, 'EEE')}
            <div className="text-[10px] sm:text-xs font-normal text-muted-foreground">
              {format(cellDate, 'dd/MM')}
            </div>
            {isNonWorking && (
              <div className="text-[9px] font-normal text-amber-600 dark:text-amber-400 leading-tight">
                Non-working
              </div>
            )}
          </th>
        );
      })}
      {showHoursToComplete && (
        <th className="p-1.5 sm:p-2 text-center font-medium text-xs sm:text-sm w-[80px] sm:w-[90px] whitespace-nowrap">
          Hrs remaining
        </th>
      )}
      <th className={`p-1.5 sm:p-2 text-center font-medium text-xs sm:text-sm ${isDaily ? 'w-[64px] sm:w-[72px]' : 'w-[52px] sm:w-[56px]'}`}>Total</th>
      <th className="p-1.5 sm:p-2 text-center font-medium text-xs sm:text-sm w-[40px]">Actions</th>
      {/* Spacer column absorbs any horizontal space left over by the fixed column widths */}
      <th ref={spacerHeaderRef} aria-hidden="true" className="p-0" />
    </tr>
  );
}
